import type { AppSettings, ExitConfigDto, JsonObject, StrategyAllocation, StrategyConfigDto, StrategyKey, StrategyLimits as StrategyLimitsSetting } from '@et/shared';
import { useEffect, useState } from 'react';
import { api, describeError } from '../api/client';
import { SchemaForm } from '../components/SchemaForm';
import { t, useT } from '../lib/i18n';
import { toast } from '../lib/toast';
import { useResource } from '../lib/use-resource';
import { useLive } from '../stores/live';
import { EXIT_GUIDE, PARAM_HELP, STRATEGY_GUIDE } from './strategy-guide';

/**
 * Strategies, explained for someone who has never traded: what each one does in three
 * steps, when it helps, what to watch out for — then only the few settings that matter,
 * with everything technical folded under "Advanced settings".
 */

const ORDER = ['trend_follower', 'technical_hunter', 'sentiment_first', 'hybrid_confirmation'];

/**
 * One per-strategy setting stored in an `app_settings` map keyed by strategy (the capital
 * pool in `allocation`, the slot cap in `strategy_limits`). Empty = no limit of its own.
 */
function StrategySettingField({ current, label, hint, inputMode, save }: {
  current: string;
  label: string;
  hint: string;
  inputMode: 'decimal' | 'numeric';
  save: (value: string | null) => Promise<void>;
}): JSX.Element {
  const [value, setValue] = useState(current);
  const [busy, setBusy] = useState(false);
  const tr = useT();
  useEffect(() => { setValue(current); }, [current]);
  const submit = async (): Promise<void> => {
    setBusy(true);
    try {
      await save(value.trim() === '' ? null : value.trim());
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="row" style={{ alignItems: 'flex-end' }}>
      <label className="field" style={{ maxWidth: 220 }}>
        {tr(label)}
        <input className="mono" inputMode={inputMode} placeholder={tr('no limit')} value={value} onChange={(e) => { setValue(e.target.value); }} />
      </label>
      <button className="small" disabled={busy || value === current} onClick={() => void submit()}>{tr('Save')}</button>
      <span className="hint muted" style={{ flex: 1, minWidth: 200 }}>{tr(hint)}</span>
    </div>
  );
}

/** Capital pool and slot cap of one strategy. */
function StrategyLimits({ strategyKey, settings, onSaved }: { strategyKey: StrategyKey; settings: AppSettings; onSaved: () => void }): JSX.Element {
  const saveAllocation = async (value: string | null): Promise<void> => {
    const next: StrategyAllocation = { ...settings.allocation };
    if (value === null) delete next[strategyKey];
    else next[strategyKey] = value;
    await api.put('/settings', { allocation: next });
    toast.success(t('Capital share saved'));
    onSaved();
  };
  const saveSlots = async (value: string | null): Promise<void> => {
    const next: StrategyLimitsSetting = { ...settings.strategy_limits };
    if (value === null) delete next[strategyKey];
    else next[strategyKey] = { maxConcurrentTrades: Number.parseInt(value, 10) };
    await api.put('/settings', { strategy_limits: next });
    toast.success(t('Max open trades saved'));
    onSaved();
  };
  const slots = settings.strategy_limits[strategyKey]?.maxConcurrentTrades;
  return (
    <>
      <StrategySettingField current={settings.allocation[strategyKey] ?? ''} label="Capital share (%)" inputMode="decimal" save={saveAllocation}
        hint="The most of the account this strategy may have invested at once. It never uses another strategy’s share." />
      <StrategySettingField current={slots === undefined ? '' : String(slots)} label="Max open trades" inputMode="numeric" save={saveSlots}
        hint="At most this many open trades for this strategy, on top of the overall limit on the risk page — so a slow strategy cannot take every place." />
    </>
  );
}

function StrategyCard({ s, onSaved, pool }: { s: StrategyConfigDto; onSaved: () => void; pool: JSX.Element | null }): JSX.Element {
  const [params, setParams] = useState<JsonObject>(s.params);
  const [busy, setBusy] = useState(false);
  const tr = useT();
  useEffect(() => { setParams(s.params); }, [s.params]);
  const guide = STRATEGY_GUIDE[s.key];
  const dirty = JSON.stringify(params) !== JSON.stringify(s.params);

  const run = async (fn: () => Promise<unknown>, ok: string, refreshLive = false): Promise<void> => {
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
      onSaved();
      if (refreshLive) await useLive.getState().loadAll();
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card stack" style={s.isActive ? { borderColor: 'var(--good)' } : undefined}>
      <div className="row">
        <h2 style={{ margin: 0, color: 'var(--text-primary)', fontSize: 17 }}>{tr(s.key)}</h2>
        {s.isActive ? (
          <>
            <span className="chip good"><span className="dot" /> {tr('Active')}</span>
            <button className="small" disabled={busy} onClick={() => void run(() => api.post(`/strategies/${s.key}/deactivate`), t('{name} stopped', { name: t(s.key) }), true)}>
              {tr('Stop this strategy')}
            </button>
          </>
        ) : (
          <button className="small primary" disabled={busy} onClick={() => void run(() => api.post(`/strategies/${s.key}/activate`), t('{name} is now active', { name: t(s.key) }), true)}>
            {tr('Turn on')}
          </button>
        )}
        {guide?.tags.map((tag) => <span key={tag} className="chip">{tr(tag)}</span>)}
      </div>

      {pool}

      {guide && (
        <>
          <div style={{ fontSize: 15 }}>{tr(guide.summary)}</div>
          <div>
            <b>{tr('How it works')}</b>
            <ol style={{ margin: '6px 0 0', paddingLeft: 20, lineHeight: 1.6 }}>
              {guide.steps.map((step) => <li key={step}>{tr(step)}</li>)}
            </ol>
          </div>
          <div className="grid cols-2" style={{ gap: 12 }}>
            <div><b className="pos">{tr('Good for')}:</b> <span className="muted">{tr(guide.goodFor)}</span></div>
            <div><b style={{ color: 'var(--warning)' }}>{tr('Watch out')}:</b> <span className="muted">{tr(guide.risk)}</span></div>
          </div>
        </>
      )}

      {s.paramSchema && (
        <>
          <div>
            <b>{tr('Main settings')}</b>
            <div style={{ marginTop: 8 }}>
              <SchemaForm schema={s.paramSchema} value={params} onChange={setParams} help={PARAM_HELP} only={guide?.main ?? []} />
            </div>
          </div>
          <details>
            <summary style={{ cursor: 'pointer', color: 'var(--text-secondary)' }}>{tr('Advanced settings')}</summary>
            <div className="muted" style={{ margin: '8px 0' }}>{tr('Every setting has a safe default. You only need to touch the main settings — or none at all.')}</div>
            <SchemaForm schema={s.paramSchema} value={params} onChange={setParams} help={PARAM_HELP} except={guide?.main ?? []} />
          </details>
        </>
      )}

      <div className="row">
        <button className="primary" disabled={busy || !dirty} onClick={() => void run(() => api.put(`/strategies/${s.key}/params`, { params }), t('{name} saved — applied without restart', { name: t(s.key) }))}>{tr('Save')}</button>
        <button disabled={busy || !dirty} onClick={() => { setParams(s.params); }}>{tr('Revert')}</button>
      </div>
    </div>
  );
}

function ExitCard({ e, onSaved }: { e: ExitConfigDto; onSaved: () => void }): JSX.Element {
  const [params, setParams] = useState<JsonObject>(e.params);
  const [busy, setBusy] = useState(false);
  const tr = useT();
  useEffect(() => { setParams(e.params); }, [e.params]);
  const dirty = JSON.stringify(params) !== JSON.stringify(e.params);

  const run = async (fn: () => Promise<unknown>, ok: string): Promise<void> => {
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
      onSaved();
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card stack" style={e.isDefault ? { borderColor: 'var(--good)' } : undefined}>
      <div className="row">
        <h2 style={{ margin: 0, color: 'var(--text-primary)', fontSize: 16 }}>{tr(e.exitKey)}</h2>
        {e.exitKey === 'trend_hold' ? (
          <span className="chip">{tr('Only for the trend follower')}</span>
        ) : e.isDefault ? (
          <span className="chip good"><span className="dot" /> {tr('in use')}</span>
        ) : (
          <button className="small" disabled={busy} onClick={() => void run(() => api.post(`/exits/${e.id}/set-default`), t('{name} is the default exit', { name: t(e.exitKey) }))}>{tr('Use for new trades')}</button>
        )}
      </div>
      <div className="muted">{tr(EXIT_GUIDE[e.exitKey] ?? '')}</div>
      {e.paramSchema && <SchemaForm schema={e.paramSchema} value={params} onChange={setParams} help={PARAM_HELP} />}
      <div className="row">
        <button className="primary" disabled={busy || !dirty} onClick={() => void run(() => api.put(`/exits/${e.id}`, { params }), t('Exit params saved'))}>{tr('Save')}</button>
        <button disabled={busy || !dirty} onClick={() => { setParams(e.params); }}>{tr('Revert')}</button>
      </div>
    </div>
  );
}

export function Strategies(): JSX.Element {
  const strategies = useResource<StrategyConfigDto[]>('/strategies');
  const exits = useResource<ExitConfigDto[]>('/exits');
  const settings = useResource<AppSettings>('/settings');
  const mode = useLive((s) => s.status?.mode);
  const tr = useT();

  return (
    <div className="stack">
      <h1>{tr('Strategies')}</h1>
      <div className="card stack" style={{ background: 'var(--surface-2)' }}>
        <b>{tr('What is a strategy?')}</b>
        <div>{tr('A strategy decides when the bot BUYS a coin. When to SELL is decided separately by the exit strategy below. You can run several strategies at once — for example the chart strategy next to a news strategy — and compare them on the Reports page. A coin is never bought twice: whichever strategy buys it first holds it.')}</div>
        <div className={mode === 'live' ? 'neg' : 'pos'}>
          {mode === 'live' ? tr('The bot is LIVE: strategies trade real money.') : tr('Right now the bot is in simulation: it trades with pretend money, so trying strategies costs nothing.')}
        </div>
      </div>

      {strategies.error && <div className="neg">{strategies.error}</div>}
      {[...(strategies.data ?? [])]
        .sort((a, b) => ORDER.indexOf(a.key) - ORDER.indexOf(b.key))
        .map((s) => (
          <StrategyCard
            key={s.key}
            s={s}
            onSaved={() => void strategies.reload()}
            pool={settings.data ? <StrategyLimits strategyKey={s.key} settings={settings.data} onSaved={() => void settings.reload()} /> : null}
          />
        ))}

      <h1 style={{ marginTop: 16 }}>{tr('Exit strategies')}</h1>
      <div className="muted">{tr('After buying, the exit strategy decides when to sell. The one marked “in use” applies to every new trade.')}</div>
      <div className="grid cols-2">
        {exits.data?.filter((e) => e.scope === 'global').map((e) => <ExitCard key={e.id} e={e} onSaved={() => void exits.reload()} />)}
      </div>
    </div>
  );
}
